import { buildI18n } from "@repo/shared";
import type { LocalizedString } from "@repo/shared/types";
import { MAX_MIN_ACCOUNT_VALUE, type WrapperType } from "@repo/shared/types";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TranslationButton } from "@/components/ui/translation-button";
import * as pcApi from "@/lib/api/portfolio-collections";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import {
  useCreateWrapperType,
  useDeleteWrapperType,
  useToggleWrapperTypeActive,
  useUpdateWrapperType,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./wrapper-types-table.i18n";

type WrapperTypesTableProps = {
  portfolioCollectionId: string;
  wrapperTypes: WrapperType[];
  /** Collection currency — the minimum account value is denominated in it. */
  currency: string;
};

/** Mirrors the server rule: null (no minimum) or a whole number > 0 within the integer column's range. */
const isValidMinAccountValue = (value: number | null) =>
  value === null || (Number.isInteger(value) && value > 0 && value <= MAX_MIN_ACCOUNT_VALUE);

export function WrapperTypesTable({
  portfolioCollectionId,
  wrapperTypes,
  currency,
}: WrapperTypesTableProps) {
  const { formatNumber } = useRegionalFormat();
  const createWrapperType = useCreateWrapperType();
  const updateWrapperType = useUpdateWrapperType();
  const deleteWrapperType = useDeleteWrapperType();
  const toggleActive = useToggleWrapperTypeActive();

  const isPending =
    createWrapperType.isPending ||
    updateWrapperType.isPending ||
    deleteWrapperType.isPending ||
    toggleActive.isPending;

  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  const getName = (i18n: LocalizedString) => localize(i18n);

  // Dialog state (shared for create + edit)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<WrapperType | null>(null);
  const [formName, setFormName] = useState("");
  const [formSyncKey, setFormSyncKey] = useState("");
  const [formMinAccountValue, setFormMinAccountValue] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const editingTypeName = editingType ? getName(editingType.nameI18n) : "";

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<string | null>(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  const openCreate = () => {
    setEditingType(null);
    setFormName("");
    setFormSyncKey("");
    setFormMinAccountValue("");
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (wt: WrapperType) => {
    setEditingType(wt);
    setFormName(getName(wt.nameI18n));
    setFormSyncKey(wt.syncKey ?? "");
    setFormMinAccountValue(wt.minAccountValue === null ? "" : String(wt.minAccountValue));
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    const name = formName.trim();
    if (!name) {
      setFormError(t.nameRequired);
      return;
    }
    const syncKey = formSyncKey.trim();
    if (!syncKey) {
      setFormError(t.syncKeyRequired);
      return;
    }
    const rawMin = formMinAccountValue.trim();
    const minAccountValue = rawMin === "" ? null : Number(rawMin);
    if (!isValidMinAccountValue(minAccountValue)) {
      setFormError(t.minAccountValueInvalid);
      return;
    }
    setFormError(null);

    if (editingType) {
      // Merge existing i18n with the updated value for the current language.
      const nameI18n: LocalizedString = { ...editingType.nameI18n, [userLanguage]: name };
      updateWrapperType.mutate(
        {
          collectionId: portfolioCollectionId,
          wtId: editingType.id,
          data: { nameI18n, syncKey, minAccountValue },
        },
        {
          onSuccess: () => setDialogOpen(false),
          onError: (err: Error) =>
            showAlert(t.error, err.message || t.failedToUpdate, "destructive"),
        },
      );
    } else {
      createWrapperType.mutate(
        {
          collectionId: portfolioCollectionId,
          data: {
            nameI18n: buildI18n(name, userLanguage, tenantSupportedLanguages),
            syncKey,
            minAccountValue,
          },
        },
        {
          onSuccess: () => setDialogOpen(false),
          onError: (err: Error) => showAlert(t.error, err.message || t.failedToAdd, "destructive"),
        },
      );
    }
  };

  const handleTranslationSave = (wt: WrapperType, nameI18n: LocalizedString) => {
    updateWrapperType.mutate(
      {
        collectionId: portfolioCollectionId,
        wtId: wt.id,
        data: { nameI18n, syncKey: wt.syncKey ?? "" },
      },
      {
        onError: (err: Error) => showAlert(t.error, err.message || t.failedToUpdate, "destructive"),
      },
    );
  };

  const handleToggleActive = (wtId: string) => {
    toggleActive.mutate(
      { collectionId: portfolioCollectionId, wtId },
      {
        onError: (err: Error) => showAlert(t.error, err.message || t.failedToToggle, "destructive"),
      },
    );
  };

  // QL-168 — a wrapper type the server would refuse to delete shows why on
  // the Delete click, before the confirmation.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: (wtId) => pcApi.checkDeleteWrapperType(portfolioCollectionId, wtId),
    onAllowed: (wtId) => {
      setTypeToDelete(wtId);
      setConfirmDeleteOpen(true);
    },
    onBlocked: (reason) => showAlert(t.error, reason || t.failedToDelete, "destructive"),
  });

  const handleDelete = () => {
    if (!typeToDelete) return;
    deleteWrapperType.mutate(
      { collectionId: portfolioCollectionId, wtId: typeToDelete },
      {
        onSuccess: () => setTypeToDelete(null),
        onError: (err: Error) => {
          showAlert(t.error, err.message || t.failedToDelete, "destructive");
          setTypeToDelete(null);
        },
      },
    );
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">{t.description}</p>
          <Button onClick={openCreate} disabled={isPending}>
            {t.addWrapperType}
          </Button>
        </div>

        {wrapperTypes.length === 0 ? (
          <EmptyState>{t.noWrapperTypes}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.syncKey}</TableHead>
                <TableHead>{t.minAccountValue}</TableHead>
                <TableHead className="w-24">{t.active}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {wrapperTypes.map((wt) => (
                <TableRow key={wt.id}>
                  <TableCell>
                    <span className="font-medium">{getName(wt.nameI18n)}</span>
                  </TableCell>
                  <TableCell>
                    {wt.syncKey ? (
                      <code className="text-xs bg-muted px-2 py-1 rounded">{wt.syncKey}</code>
                    ) : (
                      <span className="text-muted-foreground italic text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {wt.minAccountValue === null ? (
                      <span className="text-muted-foreground italic text-xs">—</span>
                    ) : (
                      <span>
                        {formatNumber(wt.minAccountValue, { maximumFractionDigits: 0 })} {currency}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      aria-label={`${t.active}: ${getName(wt.nameI18n)}`}
                      checked={wt.isActive}
                      onCheckedChange={() => handleToggleActive(wt.id)}
                      disabled={isPending}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end">
                      <Button
                        size="icon"
                        tooltip={t.editWrapperType}
                        variant="ghost"
                        aria-label={`${t.editWrapperType}: ${getName(wt.nameI18n)}`}
                        onClick={() => openEdit(wt)}
                        disabled={isPending}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <TranslationButton
                        value={wt.nameI18n}
                        onSave={(nameI18n) => handleTranslationSave(wt, nameI18n)}
                        supportedLanguages={tenantSupportedLanguages}
                        disabled={isPending}
                      />
                      <Button
                        size="icon"
                        tooltip={t.delete}
                        variant="ghost"
                        aria-label={`${t.deleteWrapperType}: ${getName(wt.nameI18n)}`}
                        loading={checkingId === wt.id}
                        onClick={() => requestDelete(wt.id)}
                        disabled={isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingType
                ? editingTypeName
                  ? `${t.editWrapperType} (${editingTypeName})`
                  : t.editWrapperType
                : t.addWrapperType}
            </DialogTitle>
            <DialogDescription>{t.createDialogDescription}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="wrapper-type-name">{t.name}</Label>
              <Input
                id="wrapper-type-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder={t.namePlaceholder}
                disabled={isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wrapper-type-sync-key">{t.syncKey}</Label>
              <Input
                id="wrapper-type-sync-key"
                value={formSyncKey}
                onChange={(e) => setFormSyncKey(e.target.value)}
                placeholder={t.syncKeyPlaceholder}
                disabled={isPending}
              />
              <p className="text-xs text-muted-foreground">{t.syncKeyDescription}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wrapper-type-min-account-value">
                {t.minAccountValue}{" "}
                <span className="text-xs font-normal text-muted-foreground">
                  ({t.minAccountValueOptional})
                </span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="wrapper-type-min-account-value"
                  type="number"
                  step="1"
                  min="1"
                  value={formMinAccountValue}
                  onChange={(e) => setFormMinAccountValue(e.target.value)}
                  disabled={isPending}
                  className="w-40"
                />
                {currency && <span className="text-muted-foreground">{currency}</span>}
              </div>
              <p className="text-xs text-muted-foreground">{t.minAccountValueDescription}</p>
            </div>

            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>

          <DialogFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button onClick={handleSubmit} loading={isPending}>
              {editingType ? t.save : t.addWrapperType}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteWrapperType}
        description={t.deleteDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
